/**
 * PURPOSE: Reglas de presentación (puras) de la tabla acumulada nueva (FTR-0065).
 * RESPONSIBILITIES: Qué deportes la tienen (familias de formatIdSport: fútbol, futsal, vóley y básquet), cuándo se
 *   muestra el sub-tab, avisos (parcial, desactualizada, cruces armados), marcas por fila, separadores por nivel,
 *   formato de valores y el movimiento de un equipo SOLO dentro de su empate perfecto.
 * COLLABORATORS: area/PosicionesArea/PosicionesArea.jsx, area/PosicionesArea/acumulada/AcumuladaStandings.jsx,
 *   CardConfigCategoryGeneral/components/ConfigForm.jsx.
 * ROUTE: winscore/src/utils/accumulatedStandingsDisplay.js
 */
import { formatIdSport } from "./formatIdSport.js";

/** Familias con tabla acumulada nueva (handball, rugby y demás no la tienen). */
export const ACCUMULATED_FAMILIES = Object.freeze([1, 2, 3, 4]);

export const isAccumulatedSport = (idsport) => ACCUMULATED_FAMILIES.includes(formatIdSport(Number(idsport)));

/** El sub-tab "Acumulada" solo existe con el flag encendido y un deporte soportado. */
export const showAccumulatedTab = (config, idsport) =>
  Boolean(config) && (config.useAcumuladoV2 === true || Number(config.useAcumuladoV2) === 1) && isAccumulatedSport(idsport);

/** Nombre completo de cada columna (tooltip de la abreviatura del encabezado). */
export const COLUMN_TITLES = Object.freeze({
  pj: "Partidos jugados",
  pts: "Puntos",
  ptsPerMatch: "Puntos por partido",
  g: "Partidos ganados",
  p: "Partidos perdidos",
  winsPerMatch: "Partidos ganados por partido jugado",
  dp: "Diferencia (goles o puntos)",
  dgPerMatch: "Diferencia de goles por partido",
  dpPerMatch: "Diferencia de puntos por partido",
  pf: "A favor (goles o puntos)",
  pc: "En contra (puntos)",
  gfPerMatch: "Goles a favor por partido",
  pfPerMatch: "Puntos a favor por partido",
  setG: "Sets ganados",
  setP: "Sets perdidos",
  ratioSet: "Ratio de sets (ganados / perdidos)",
  ratioPuntos: "Ratio de puntos (a favor / en contra)",
});

/** Iniciales de respaldo cuando un equipo no tiene logo (o no carga). */
export const teamInitials = (team) => {
  const text = String(team?.abbreviation || team?.name || "").replace(/[^\p{L}\p{N} ]/gu, "").trim();
  if (!text) return "?";
  const words = text.split(/\s+/);
  return (words.length > 1 ? words.slice(0, 2).map((word) => word[0]).join("") : text.slice(0, 2)).toUpperCase();
};

/** Avisos sobre la tabla (en el orden en que se muestran). */
export const accumulatedNotices = (view) => {
  if (!view?.available) return [];
  const notices = [];
  if (view.pendingGroupMatches > 0) {
    notices.push({ key: "partial", tone: "amber", text: `Parcial: faltan ${view.pendingGroupMatches} partidos de grupos` });
  }
  if (view.stored && !view.stored.inSync) {
    notices.push({ key: "stale", tone: "red", text: "Tabla desactualizada", action: "recalculate" });
  }
  if (view.qualificationRefs > 0) {
    notices.push({
      key: "crosses",
      tone: "slate",
      text: "Los cruces ya armados con la acumulada (n.º acumulado) no se actualizan solos si la tabla cambia: revísalos.",
    });
  }
  return notices;
};

/** Marcas de una fila: "Solo WO", "Sin partidos", "Empate", "incluye N WO". */
export const rowBadges = (row) => {
  const badges = [];
  if (row.walkoverOnly) badges.push({ key: "wo-only", tone: "slate", text: "Solo WO" });
  if (row.noMatches) badges.push({ key: "no-matches", tone: "slate", text: "Sin partidos" });
  if (row.tie) badges.push({ key: "tie", tone: "amber", text: row.tie.manual ? "Empate (orden manual)" : "Empate" });
  if (row.walkoverMatches > 0) badges.push({ key: "wo", tone: "slate", text: `incluye ${row.walkoverMatches} WO` });
  return badges;
};

/** Filas agrupadas por nivel (separadores "1.º de grupo", "2.º de grupo"… y el bloque "Solo W.O."). */
export const sectionsByLevel = (rows = []) => {
  const sections = [];
  for (const row of rows) {
    const key = `${row.walkoverOnly ? "wo" : "play"}:${row.level}`;
    const last = sections[sections.length - 1];
    if (last && last.key === key) last.rows.push(row);
    else sections.push({ key, label: row.levelLabel || `${row.level}.º de grupo`, rows: [row] });
  }
  return sections;
};

/** Valor de una celda: "Max.", número con hasta 3 decimales, o "—" cuando no hay partidos. */
export const formatAccumulatedValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  return String(Math.round(Number(value) * 1000) / 1000);
};

/** Valor de una columna para una fila: totales en `values`, por partido / ratios en `display`. */
export const columnValue = (row, columnKey) => (columnKey in (row.display || {}) ? row.display[columnKey] : row.values?.[columnKey]);

/**
 * Nuevo orden del empate al mover un equipo arriba (-1) o abajo (+1), SOLO entre los equipos de su mismo empate
 * perfecto (misma firma). null si no se puede mover (no empatado o en el borde).
 */
export const moveWithinTie = (rows, idparticipant, direction) => {
  const row = rows.find((item) => item.idparticipant === idparticipant);
  if (!row?.tie) return null;
  const members = rows.filter((item) => item.tie?.signature === row.tie.signature).map((item) => item.idparticipant);
  const index = members.indexOf(idparticipant);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= members.length) return null;
  const next = [...members];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};
